import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { BrandMark } from "../components/Brand";
import TrustCues from "../components/TrustCues";
import { CheckCircle2, Mail, Clock, Phone, ShieldCheck } from "lucide-react";

export default function ApplySuccess() {
  const [params] = useSearchParams();
  const attorney = params.get("attorney") || "your attorney";
  const id = params.get("id");

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="apply-success-page">
      <header className="border-b border-[#E5E7EB]">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 h-16 flex items-center">
          <Link to="/"><BrandMark /></Link>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-6 py-16 lg:py-20">
        <div className="max-w-3xl w-full animate-fade-up">
          {/* Animated confirmation icon */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-[#ECFDF5] animate-ping opacity-30" />
              <div className="relative inline-flex items-center justify-center w-24 h-24 rounded-full bg-[#ECFDF5] border-4 border-[#A7F3D0]">
                <CheckCircle2 className="w-12 h-12 text-[#047857]" strokeWidth={2.5} />
              </div>
            </div>
          </div>

          <div className="text-center">
            <div className="uppercase-label mb-3">Application received</div>
            <h1 className="font-display text-4xl lg:text-6xl text-[#0A2540] font-semibold tracking-tight leading-[1.05]">
              You're all set.
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-[#374151] leading-relaxed max-w-2xl mx-auto">
              We're contacting <span className="text-[#0A2540] font-medium">{attorney}</span> right now to verify your case and start underwriting. Most decisions are made within 24 hours.
            </p>
          </div>

          {/* Timeline */}
          <div className="mt-12 grid sm:grid-cols-3 gap-4">
            <TimelineCard step="1" t="Confirmation sent" d="Check your email — we just sent a copy of your application." icon={Mail} done />
            <TimelineCard step="2" t="Attorney verifying" d="Secure verification link emailed to your attorney." icon={ShieldCheck} done />
            <TimelineCard step="3" t="Decision in 24 hrs" d="We'll text and email you with next steps." icon={Clock} />
          </div>

          {/* Info */}
          <div className="mt-12 bg-[#0A2540] text-white rounded-2xl p-7 lg:p-9 relative overflow-hidden">
            <div className="absolute -right-20 -bottom-20 w-72 h-72 rounded-full bg-[#0066FF]/30 blur-3xl" />
            <div className="relative grid sm:grid-cols-2 gap-6 items-center">
              <div>
                <div className="uppercase-label !text-white/60 mb-2">Reference ID</div>
                <div className="font-mono text-2xl font-semibold tracking-wider" data-testid="success-app-id">
                  {id ? id.slice(0, 8).toUpperCase() : "—"}
                </div>
                <p className="text-sm text-white/70 mt-3">Save this for your records. Reference it when you call.</p>
              </div>
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex items-center gap-2 text-white/80"><Phone className="w-4 h-4 text-[#0066FF]" /> (216) 245-5967</div>
                <div className="flex items-center gap-2 text-white/80"><Mail className="w-4 h-4 text-[#0066FF]" /> hello@instabridgefunding.com</div>
                <div className="text-xs text-white/60 mt-1">Mon–Fri 9 am – 6 pm ET</div>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/"><Button variant="outline" className="rounded-full" data-testid="success-home-btn">Back to homepage</Button></Link>
            <Link to="/faq"><Button variant="ghost" className="rounded-full">Read FAQ</Button></Link>
          </div>

          {/* Reassurance: payout-method strip on the confirmation page */}
          <div className="mt-10 flex justify-center">
            <TrustCues variant="payout-strip" audience="consumer" testid="success-payout-strip" />
          </div>

          <p className="text-center text-xs text-[#9CA3AF] mt-12">
            Questions? Reply to your confirmation email or call us. We're real humans, available 9–6 ET.
          </p>
        </div>
      </div>
    </div>
  );
}

const TimelineCard = ({ step, t, d, icon: Icon, done = false }) => (
  <div className={`relative bg-white border rounded-xl p-5 ${done ? "border-[#A7F3D0] bg-[#F0FDF4]" : "border-[#E5E7EB]"}`}>
    <div className={`absolute -top-2 -left-2 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold ${
      done ? "bg-[#047857] text-white" : "bg-white border border-[#E5E7EB] text-[#0A2540]"
    }`}>{done ? <CheckCircle2 className="w-4 h-4" /> : step}</div>
    <Icon className={`w-5 h-5 ${done ? "text-[#047857]" : "text-[#0066FF]"}`} />
    <div className="font-display font-semibold text-[#0A2540] mt-3">{t}</div>
    <p className="text-xs text-[#6B7280] mt-1 leading-relaxed">{d}</p>
  </div>
);
